/**
 * Trustico® Hosted SSL Certificate Issuance Tool - Configuration
 *
 * Constants shared across the Issuance Tool. No DOM, no React. Pure
 * ES module ; safe to import from server code or browser code.
 *
 * Every constant here is the single source of truth for both the
 * browser (Step 2 form validation, Configuration File parse) and
 * the server (validateInputs in
 * src/server/services/issuance/validation.js). Changes to a constant
 * in this file propagate to both sides through the module system -
 * there is no separate "keep in sync" copy, with ONE exception : the
 * server allow-list ALLOWED_ACME_SERVERS in that same validation.js
 * is a hand-written mirror of ACME_SERVERS below, so a change to the
 * service list is made in both files.
 */

/**
 * Allowed Automatic Certificate Management Environment (ACME) service
 * endpoints. This is the exclusive list - no other Certificate Authority
 * endpoints are permitted. Customers cannot enter a custom URL.
 */
export const ACME_SERVERS = [
  {
    id: "trustico-dv",
    label: "Trustico® Domain Validation (DV)",
    // Plain-language card description (2026-08-13 go-public content
    // ruling) : the cards previously distinguished themselves only by
    // a raw directory URL. DEFINED here beside the label so the four
    // stay single-source with the service list.
    description:
      "For Trustico® branded Domain Validation (DV) SSL Certificates. Validates control of your licensed domain names and is the standard choice for most websites.",
    url: "https://acme.trust-provider.com/v2/TrusticoDV",
    eabRequired: true,
  },
  {
    id: "trustico-ov",
    label: "Trustico® Organization Validation (OV)",
    description:
      "For Trustico® branded Organization Validation (OV) SSL Certificates. These include verified company identity details and display the organization name within the SSL Certificate.",
    url: "https://acme.trust-provider.com/v2/TrusticoOV",
    eabRequired: true,
  },
  {
    id: "sectigo-dv",
    label: "Sectigo® Domain Validation (DV)",
    description:
      "For Sectigo® branded Domain Validation (DV) SSL Certificates. Validates control of your licensed domain names and is the standard choice for most websites.",
    url: "https://acme.sectigo.com/v2/DV",
    eabRequired: true,
  },
  {
    id: "sectigo-ov",
    label: "Sectigo® Organization Validation (OV)",
    description:
      "For Sectigo® branded Organization Validation (OV) SSL Certificates. These include verified company identity details and display the organization name within the SSL Certificate.",
    url: "https://acme.sectigo.com/v2/OV",
    eabRequired: true,
  },
];

/** Default Automatic Certificate Management Environment (ACME) server selection at page load. */
export const DEFAULT_ACME_SERVER_ID = "trustico-dv";

/**
 * SSL Certificate key types offered to customers in Step 2.
 *
 * SERVICE-CONTRACT NOTE (2026-07-05, from Sectigo®'s own client docs,
 * docs/sectigo-spacme/configuring-the-acme-client.md "Config file
 * reference") : the CaaS service supports RSA2048, RSA3072, RSA4096,
 * RSA8192, EC256, and EC384. We offer ALL SIX (operator decision
 * 2026-07-05 : "if Sectigo has 6 key types and that's in their docs
 * then we need to offer it") - the full documented service contract,
 * not a subset. Server-side generation cost is a non-issue : RSA-8192,
 * the slowest, measured ~2.7s locally (see the RSA-8192 note on
 * CSR_DIGEST_BY_KEY_TYPE_ID in src/server/services/acme/crypto.js)
 * and the startIssuance convergent-retry design absorbs even a slow
 * outlier by persisting the generated key before any budget trip.
 * ALLOWED_KEY_TYPES on the server derives from this array, so this
 * list is the single source of truth for the whole stack.
 */
export const KEY_TYPES = [
  {
    id: "rsa-2048",
    label: "RSA 2048 bits (Recommended)",
    algorithm: "RSA",
    bits: 2048,
  },
  {
    id: "rsa-3072",
    label: "RSA 3072 bits",
    algorithm: "RSA",
    bits: 3072,
  },
  {
    id: "rsa-4096",
    label: "RSA 4096 bits",
    algorithm: "RSA",
    bits: 4096,
  },
  {
    id: "rsa-8192",
    label: "RSA 8192 bits",
    algorithm: "RSA",
    bits: 8192,
  },
  {
    id: "ecc-p256",
    label: "ECC P-256",
    algorithm: "ECC",
    curve: "P-256",
  },
  {
    id: "ecc-p384",
    label: "ECC P-384",
    algorithm: "ECC",
    curve: "P-384",
  },
];

export const DEFAULT_KEY_TYPE_ID = "rsa-2048";

/**
 * Automatic Certificate Management Environment (ACME) account key parameters.
 * Fixed - not customer-selectable. ECDSA P-256 is the conventional account
 * key shape for ACME clients.
 */
export const ACME_ACCOUNT_KEY = {
  algorithm: "ECDSA",
  namedCurve: "P-256",
};

/**
 * External Account Binding (EAB) credential format validation.
 *
 * Character set : base64url alphabet (letters, digits, "-", "_")
 * plus optional trailing "=" padding. Standard-base64 "+" / "/"
 * characters in customer input are normalised to "-" / "_" by
 * normaliseBase64Url (below) before this regex is applied,
 * so customers with legacy credentials carrying the standard
 * alphabet are not locked out.
 *
 * Length window : 8 to 512 characters. The lower bound rejects
 * obvious empty / truncated values ; the upper bound bounds memory
 * use and matches what real-world EAB credentials look like.
 */
// base64url alphabet only (RFC 4648 §5) - letters / digits / "-" / "_".
// The 2026-06-01 third-round code review #5 found that the previous
// regex /^[A-Za-z0-9_\-+/]+=*$/ also accepted the "+" and "/" of
// standard base64 ; Sectigo® has been observed to bounce EAB
// credentials carrying those characters with a generic "Invalid EAB
// Credentials" error. The regex therefore defines the CANONICAL
// post-normalisation alphabet : both validators run
// normaliseBase64Url (below) first, so a legacy standard-base64
// credential is translated rather than rejected, and the reject-up-
// front behaviour applies only to input that is invalid in EITHER
// alphabet. (2026-08-14 go-public review, "validation-drift" : the
// browser previously applied this regex WITHOUT the normalisation
// the server performs, locking out exactly the legacy-credential
// customers the server-side normalisation was written for.) The
// cPanel plugin matches the same alphabet in
// trustico_is_valid_eab_format.
export const EAB_REGEX = /^[A-Za-z0-9_-]+=*$/;
export const EAB_MIN_LENGTH = 8;
export const EAB_MAX_LENGTH = 512;

/**
 * The customer-facing cooldown discriminators (2026-08-14 go-public
 * review) : machine-readable `code` values the server's error
 * envelope may surface and the browser deliberately branches on
 * (info-notice treatment instead of the red failure Alert). Hoisted
 * to the shared tier because the SAME literal must agree at every
 * site across both tiers - the errorHandler allow-list
 * (CUSTOMER_ERROR_CODES), the producers (buildTriggerCooldownError
 * and buildCheckCooldownError in
 * src/server/services/issuance/limits.js, and the finalize poll
 * cooldown in finalizeAndIssue), and the consumers (ChallengePanel
 * for the trigger and check codes, Step4Download for the finalize
 * poll code) - and a rename that misses one silently returns the
 * cooldown to the red failure treatment. The route tests pin the WIRE
 * value with string literals on purpose, so a drift here still goes
 * red there.
 */
export const TRIGGER_COOLDOWN_CODE = "TRIGGER_COOLDOWN";
export const FINALIZE_POLL_COOLDOWN_CODE = "FINALIZE_POLL_COOLDOWN";
export const CHECK_COOLDOWN_CODE = "CHECK_COOLDOWN";
// The Certificate Authority (CA) refused the External Account Binding
// (EAB) credentials at registration (2026-08-27). Step 2 shows its
// wrong-service hint ONLY under this code : under every other failure
// (a transient at the CA, a timeout) the hint pointed the customer at
// the one thing that was not wrong, which is what the operator hit on
// the live run that prompted this.
export const EAB_REJECTED_CODE = "EAB_REJECTED";

/**
 * Minimum interval between two POST-SUBMIT finalize polls (normally
 * the read-only order GET that asks "has the CA signed yet?"). A
 * COOLDOWN rather than a lifetime counter ON PURPOSE (2026-08-14
 * go-public review) : any monotonic per-session cap on the
 * post-submit poll re-creates the uncollectable-SSL-Certificate
 * wedge - the CSR is submitted, the CA is signing, and a spent
 * counter would block the only call that can ever hand the customer
 * their SSL Certificate. A cooldown can never wedge : it bounds the
 * per-session CA-facing poll rate to one per window forever, absorbs
 * a double-click, and its 429 tells the customer to wait a few
 * seconds - advice that is actually true. 5 seconds sits below human
 * re-click patience on Step 4 while holding even a scripted session
 * to 720 CA polls per hour.
 *
 * LIVES IN THE SHARED TIER (2026-08-14 go-public review) : the value
 * is server tuning, but two readers outside the server need it. The
 * Pebble e2e driver derives its wait-past-the-window sleep from it,
 * and importing the whole service module for one number executed the
 * service's boot assertions and store imports inside the driver
 * process. Step 4 in the browser (Step4Download) starts its button
 * countdown from the same number.
 */
export const FINALIZE_POLL_COOLDOWN_MS = 5_000;

/**
 * Minimum interval between two "Request Validation" (challenge
 * re-trigger) requests on the SAME challenge. Sectigo® performs exactly
 * ONE validation attempt per trigger and does NOT auto-retry ; a second
 * trigger arriving before the first attempt completes RESETS Sectigo®'s
 * in-flight lookup. That is precisely why the old fused "Check Now"
 * (which re-triggered on every click) could wedge a challenge in
 * "processing" forever. This floor keeps the trigger sparse - mirroring
 * the acme.sh fork's patient 60s..10min re-trigger cadence
 * (acme.sh:5230 / :5247) - while the read-only status poll
 * (checkChallenge) carries its own, much shorter cooldown -
 * CHECK_COOLDOWN_MS in the shared config.
 *
 * FIVE MINUTES since 2026-08-16 (operator directive). It was 60
 * seconds, the bottom of the acme.sh range ; the operator raised it
 * toward the patient end. That suits what the trigger actually does :
 * a second trigger inside the window does not merely waste a call, it
 * RESETS the Certificate Authority's in-flight attempt, so the press a
 * customer makes out of impatience is the one that sets their own
 * validation back. A longer floor protects them from that, and the
 * read-only status poll stays the fast way to ask whether anything has
 * happened.
 *
 * Lives in the shared tier so the browser and the server agree, the
 * same placement rationale as CHECK_COOLDOWN_MS below. The browser
 * needs it to start the Request Validation countdown the moment a
 * trigger succeeds, without waiting to be refused to learn the number
 * (operator ruling 2026-08-22).
 */
export const TRIGGER_COOLDOWN_MS = 300_000;

/**
 * Minimum interval between two "Check Validation Status" polls on the
 * SAME challenge (operator directive 2026-08-16 : "the check
 * validation status 30 seconds perhaps").
 *
 * The poll used to be deliberately unthrottled - it is read-only and
 * cannot disturb the Certificate Authority's in-flight validation, so
 * a customer could ask "is it done yet" as often as they liked. What
 * that overlooked is the per-session ceiling above it
 * (CHECK_NOW_MAX_PER_SESSION) : every press spends one of those, and
 * an impatient customer could burn the whole allowance in a couple of
 * minutes of clicking and then be told there were too many checks -
 * the exhaustion landing on someone who had done nothing wrong.
 *
 * A cooldown protects that budget instead of rationing it after the
 * fact. 30 seconds is well under the time a Certificate Authority
 * takes to complete a validation, so it never makes a customer wait
 * for information that exists ; it only absorbs the repeat pressing
 * that produced nothing anyway. It also spreads the session ceiling
 * across hours rather than minutes.
 *
 * A COOLDOWN, never a counter, for the same reason
 * FINALIZE_POLL_COOLDOWN_MS is one : a spent counter can wedge a
 * customer out of the only call that reports their progress, and a
 * cooldown cannot.
 *
 * Lives in the shared tier so the browser and the server agree, the
 * same placement rationale as FINALIZE_POLL_COOLDOWN_MS above.
 */
export const CHECK_COOLDOWN_MS = 30_000;

/**
 * Join a challenge array's Certificate Authority (CA) error details
 * into ONE customer-facing diagnostic line (2026-08-14 go-public
 * review) : the server's getChallengeDetails errorDetail passthrough
 * and the browser's ChallengePanel.runCheck live-invalid branch must
 * present the SAME text for the same authorization, so the filter and
 * the " ; " house separator live here once instead of as two
 * byte-identical copies that drift on the first change. The CA's own
 * wording is surfaced verbatim - rewriting it would destroy the
 * customer's diagnostic information. Returns "" when no challenge
 * carries a detail ; each caller applies its own fallback (the server
 * ships null, the browser shows NO_CA_REASON_FALLBACK). Pure data
 * transformation : tier-neutral by construction.
 */
export function joinChallengeErrorDetails(challenges) {
  // c?. as well as error?. (2026-08-14 post-merge review item) : the
  // array comes straight off a Certificate Authority response, and a
  // null element - legal JSON - would otherwise throw a TypeError
  // that escapes the server path as an unbranded 500.
  return (challenges || [])
    .map((c) => c?.error?.detail)
    .filter(Boolean)
    .join(" ; ");
}

/**
 * The name of a challenge-detail entry AS THE CUSTOMER ASKED FOR IT.
 *
 * An ACME authorization identifies a wildcard by the BARE domain plus
 * a `wildcard: true` flag - RFC 8555 §7.1.4 - so an order for
 * "*.example.com" and "example.com" produces two authorizations whose
 * `identifier.value` is the same string. The asterisk only exists on
 * our side of the wire, and every surface that shows a domain has to
 * put it back.
 *
 * OPERATOR-FOUND, 2026-08-22, on his live run : "the message says
 * trustico.dev when the actual domain that was validated was
 * *.trustico.dev". Three surfaces rebuilt the name correctly by hand
 * and Step 3's already-authorized notice did not, so it announced the
 * validated WILDCARD authorization under the bare name - directly
 * above a panel for the genuinely-pending BARE domain, which is the
 * same string. The two are different authorizations ; on screen they
 * read as one domain both finished and awaiting work.
 *
 * DERIVED, NOT HAND-COPIED. A fourth copy of `wildcard ? "*." : ""` is
 * how a fifth surface gets it wrong, and the defect being fixed here
 * IS the missing copy (pattern-sweep Rule #1a : a hand-written list
 * goes stale silently and reads as protection).
 *
 * @param {{domain?: string, wildcard?: boolean}} entry - a
 *   /challenges entry, or anything carrying the same two fields.
 * @returns {string} e.g. "*.example.com" or "example.com"
 */
export function challengeDomainLabel(entry) {
  if (!entry || !entry.domain) return "";
  return (entry.wildcard ? "*." : "") + entry.domain;
}

/**
 * Translate standard base64 characters to their base64url
 * equivalents : "+" -> "-" and "/" -> "_". Both the browser's Step 2
 * validateEab and the server's validateInputs run this BEFORE
 * applying EAB_REGEX, so a customer who pasted credentials with the
 * standard-base64 alphabet (e.g. from a password-manager snapshot
 * taken before the credential-issuance system switched to base64url)
 * is not locked out. 2026-06-02 fourth-round code review #7 ;
 * HOISTED here from issuanceService.js by the 2026-08-14 go-public
 * review ("validation-drift") so the two sides can never disagree
 * about the normalisation again. Pure string translation :
 * tier-neutral by construction.
 */
export function normaliseBase64Url(s) {
  if (typeof s !== "string") return s;
  return s.replace(/\+/g, "-").replace(/\//g, "_");
}

/** Maximum domains per SSL Certificate. Matches the cPanel plugin cap. */
export const MAX_DOMAINS_PER_CERTIFICATE = 100;

/**
 * Domain name validation regex. Permits Latin-script labels per
 * RFC 1035 §2.3.1 (1 to 63 chars of letters / digits / hyphens, each
 * label separated by a dot). Wildcard handling (the "*." prefix) is
 * stripped before this regex runs. Used by both the browser-side
 * Step 2 form and the server-side validateInputs.
 */
export const DOMAIN_REGEX =
  /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

/**
 * House e-mail format regex. Permissive enough to accept any
 * real-world address but strict enough to reject obvious
 * mistakes ("plain", "a@b", "missing@dot") that the browser's
 * own HTML5 validator misses.
 *
 * 2026-05-31 seventh-round code review nit-3 : pattern-sweep
 * partner of DOMAIN_REGEX above. Previously this lived as a
 * file-local constant in src/client/components/issuance/Step2Configure.jsx,
 * which was exactly the drift risk this shared module was put
 * here to prevent.
 *
 * Its consumer is the CAA Record Generator's iodef address
 * (src/server/services/caaService.js). It stays in this shared module
 * rather than moving into caaService because the strictness decision
 * below is a fleet-wide house rule, not a CAA-specific one.
 */
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// 2026-06-01 : the former RESERVED_DOMAIN_SUFFIXES hand-rolled
// blocklist was removed in favour of real Top-Level Domain (TLD)
// validation against the Public Suffix List (tldts). The realness
// gate now lives in src/shared/utils/domainValidation.js
// (isRegistrablePublicDomain) and is shared by the browser-side
// Step 2 form and the server-side validateInputs. This matches how
// the sibling projects tracking-01 and middleware-01 validate
// domains and AFFIRMATIVELY requires a real ICANN TLD rather than
// denying a short list of special-use names.

/**
 * Per-field byte caps applied at both Configuration File parse time
 * (configFile.js parseConfigFile) AND at server-side session input
 * validation (validateInputs in
 * src/server/services/issuance/validation.js). The 2026-05-31
 * code review #3 found that the parseConfigFile caps were not
 * mirrored on the server, so a client bypassing the browser flow
 * could POST a near-1 MB Privacy-Enhanced Mail (PEM) blob bounded
 * only by the body-parser limit. These constants are now the
 * single source of truth for both paths.
 *
 * - PEM_LENGTH covers PEM account / certificate private keys.
 *   RSA-4096 PKCS#8 PEM is around 3.4 Kilobytes (KB) ; 16 KB
 *   leaves comfortable headroom for future key types without
 *   permitting a multi-megabyte blob.
 * - URL_LENGTH is the 2 KB practical browser / server limit
 *   (Microsoft Internet Information Services (IIS) defaults, AWS
 *   Application Load Balancer, NGINX) and well over any plausible
 *   ACME URL.
 * - DOMAIN_LENGTH matches the RFC 1035 §2.3.4 label-stream maximum.
 *
 * There is deliberately no e-mail cap here : the CAA Record Generator
 * caps its iodef address with its own MAX_IODEF_EMAIL_LENGTH, and it
 * is the only consumer. Add one here only if a second tool needs the
 * same cap (2026-08-13 go-public "dead-export" audit : an exported
 * constant nothing imports is drift waiting to happen).
 */
export const MAX_PEM_LENGTH = 16384;
export const MAX_URL_LENGTH = 2048;
export const MAX_DOMAIN_LENGTH = 253;
export const MAX_SERVER_ID_LENGTH = 64;
export const MAX_KEY_TYPE_ID_LENGTH = 64;

// NOTE : there is deliberately no polling-cadence constant. Domain
// validation and certificate signing are driven by the customer
// pressing the check buttons on the page, not by an automatic
// poll loop. See the checkChallenge note in
// src/server/services/issuanceService.js for the rationale.

/** Configuration File schema version. Bump when the schema changes. */
export const CONFIG_FILE_SCHEMA_VERSION = 2;

/** Configuration File extension and Multipurpose Internet Mail Extensions (MIME) type. */
export const CONFIG_FILE_EXTENSION = "trustico-config.json";
export const CONFIG_FILE_MIME = "application/json";

/**
 * localStorage namespace for in-progress issuance state.
 *
 * 2026-05-31 seventh-round code review nit-1 : the "-v1" suffix
 * here does NOT track the payload's SCHEMA_VERSION (which now
 * stands at 2, see src/client/issuance/state.js). The storage key
 * namespaces the LOCATION the data lives at ; the
 * SCHEMA_VERSION inside the payload describes the data's
 * SHAPE. They evolve independently :
 *
 *   - SCHEMA_VERSION bumps every time the saved payload's
 *     fields change. The state.js loader (loadState) ignores a
 *     payload whose version does not match and returns null, as
 *     if nothing were saved ; it does not remove the slot.
 *   - STATE_STORAGE_KEY only changes if we deliberately want to
 *     orphan every customer's saved session (e.g. a privacy
 *     reset, a key-conflict with another tool). Bumping it
 *     loses every in-progress order across the entire customer
 *     base, which is almost never the right move.
 *
 * Leaving the key at "-v1" is intentional - the SCHEMA_VERSION
 * already covers shape evolution.
 */
export const STATE_STORAGE_KEY = "trustico-issuance-v1";

/**
 * Session lifetime in HOURS, from CREATION (absolute cap - re-reads
 * never extend it past this). Hoisted to the shared tier in the
 * 2026-08-13 go-public cleanup so the CUSTOMER-FACING copy (the
 * Step 3 "Need to Step Away" note telling customers how long their
 * issuance request stays open) cites the same source of truth the server
 * enforces : the server-side SESSION_TTL_MS in
 * src/server/services/sessionStore.js derives from this constant, and
 * the tier boundary forbids the client importing that module
 * directly. Change the lifetime here and the store, the cookie, the
 * limiter window, and the customer copy all move together.
 */
export const SESSION_LIFETIME_HOURS = 48;

/** Step identifiers used by the UI controller. */
export const STEPS = {
  SERVICE: 1,
  CONFIGURE: 2,
  VERIFY: 3,
  DOWNLOAD: 4,
};
